// src/components/dashboard/ReviewHeatmap.js
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { PaperAirplaneIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import ChartHeader from '../common/ChartHeader';
import { displayListens } from '../../utils/listeningStats';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Restyled tooltip: Solid background, no glass effect, and animated
const HeatmapTooltip = ({ visible, x, y, date, value, ratio, title, isProtectedDay, isVacationDay }) => {
  if (!visible) return null;

  const formattedDate = moment(date).format('MMM D, YYYY');

  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={{
        left: x,
        top: y - 10,
        transform: 'translate(-50%, -100%)',
      }}
    >
      <div className="p-3 bg-background-secondary shadow-xl rounded-lg border border-border dark:bg-background-secondary animate-fade-in">
        <div className="text-sm font-medium text-content-primary mb-1">{formattedDate}</div>
        {isVacationDay ? (
          <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'var(--color-glassy-purple)' }}>
            <PaperAirplaneIcon className="h-3.5 w-3.5 flex-shrink-0" />
            Vacation — streak paused
          </div>
        ) : isProtectedDay ? (
          <div className="flex items-center gap-1.5 text-xs font-medium text-sky-600 dark:text-sky-300">
            <span lang="ja" className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-sky-500 text-[10px] font-bold leading-none text-white">盾</span>
            Streak protected
          </div>
        ) : (
          <div className="text-xs text-content-secondary">
            {value} {title.toLowerCase()} {value === 1 ? 'item' : 'items'}
            {ratio != null && <span className="mt-0.5 block">{displayListens(ratio).toFixed(1)} plays/review</span>}
          </div>
        )}
      </div>
    </div>
  );
};

// A dedicated empty state component
const HeatmapEmptyState = ({ emptyMessage, title }) => (
  <div className="group relative flex flex-grow flex-col overflow-hidden rounded-xl border border-border bg-background-secondary p-4">
    <div className="absolute inset-0 opacity-10">
      <div className="absolute right-0 top-0 h-24 w-24 -translate-y-12 translate-x-12 rounded-full bg-gradient-to-br from-primary to-secondary transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute bottom-0 left-0 h-16 w-16 -translate-x-8 translate-y-8 rounded-full bg-gradient-to-br from-accent to-primary transition-transform duration-700 group-hover:scale-110" />
    </div>
    <div className="relative z-10">
      <ChartHeader title={title} />
    </div>
    <div className="relative z-10 flex flex-grow flex-col items-center justify-center text-center text-content-secondary">
      <div className="relative mb-3 flex h-16 w-16 items-center justify-center rounded-full border-2 border-white/30 bg-gradient-to-br from-gray-100 to-gray-200 backdrop-blur-sm dark:from-gray-700 dark:to-gray-800">
        <div className="h-6 w-6 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-500 dark:to-gray-600" />
        <div className="absolute inset-0 rounded-full border border-dashed border-gray-400 opacity-50" />
      </div>
      <p className="mb-1 text-sm font-semibold">No activity yet</p>
      <p className="text-xs opacity-80">{emptyMessage}</p>
    </div>
  </div>
);

const ReviewHeatmap = ({ title, data, playsData, colorVar, emptyMessage, protectedDates, vacationDates }) => {
  const protectedDays = protectedDates instanceof Set ? protectedDates : new Set(protectedDates || []);
  const vacationDays = vacationDates instanceof Set ? vacationDates : new Set(vacationDates || []);

  // 0 = current month; +1 = previous month, etc. (paged via the arrows)
  const [monthOffset, setMonthOffset] = useState(0);

  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    date: '',
    value: 0,
    ratio: null,
    isProtectedDay: false,
    isVacationDay: false,
  });

  const allTimeTotal = useMemo(() => Object.values(data || {}).reduce((sum, v) => sum + v, 0), [data]);

  // How far back the user can page: from the current month to the earliest activity month.
  const maxOffset = useMemo(() => {
    const keys = Object.keys(data || {});
    if (!keys.length) return 0;
    const earliestKey = keys.reduce((min, k) => (k < min ? k : min), keys[0]);
    const earliest = moment(earliestKey, 'YYYY-MM-DD');
    if (!earliest.isValid()) return 0;
    return Math.max(0, moment().startOf('month').diff(earliest.startOf('month'), 'months'));
  }, [data]);

  const offset = Math.min(monthOffset, maxOffset);

  // A fixed 6-week (42-day) window starting on the Sunday on/before the 1st, so the
  // grid is always the same height (no layout shift when paging). Days outside the
  // shown month are rendered dimmed instead of as empty "dead space".
  const month = useMemo(() => {
    const today = moment();
    const monthStart = today.clone().startOf('month').subtract(offset, 'month');
    const gridStart = monthStart.clone().startOf('week');
    const days = [];
    let monthMax = 0;
    let total = 0;
    let plays = 0;
    let reviewsWithPlays = 0;
    for (let i = 0; i < 42; i++) {
      const day = gridStart.clone().add(i, 'day');
      const date = day.format('YYYY-MM-DD');
      const value = data[date] || 0;
      const inMonth = day.isSame(monthStart, 'month');
      if (inMonth) {
        monthMax = Math.max(monthMax, value);
        total += value;
        // Ratio only over days that have plays data (plays start when tracking does).
        if (playsData && playsData[date] != null) {
          plays += playsData[date];
          reviewsWithPlays += value;
        }
      }
      days.push({
        date,
        dayOfMonth: day.date(),
        inMonth,
        isToday: day.isSame(today, 'day'),
        isFuture: day.isAfter(today, 'day'),
      });
    }
    const ratio = reviewsWithPlays > 0 ? plays / reviewsWithPlays : null;
    return { label: monthStart.format('MMMM YYYY'), days, monthMax, total, ratio };
  }, [data, offset, playsData]);

  // 0 → no color; the month's busiest day → full color (level 4); rest scales between.
  const getIntensity = (value) => {
    if (value <= 0 || month.monthMax <= 0) return 0;
    return Math.min(4, Math.ceil((value / month.monthMax) * 4));
  };

  const handleMouseEnter = (event, day) => {
    const rect = event.target.getBoundingClientRect();
    const value = data[day.date] || 0;
    const dayPlays = playsData?.[day.date];

    setTooltip({
      visible: true,
      x: rect.left + rect.width / 2,
      y: rect.top,
      date: day.date,
      value,
      ratio: dayPlays != null && value > 0 ? dayPlays / value : null,
      isProtectedDay: value <= 0 && protectedDays.has(day.date),
      isVacationDay: value <= 0 && vacationDays.has(day.date),
    });
  };

  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  const canGoOlder = offset < maxOffset;
  const canGoNewer = offset > 0;

  return (
    <>
      <div className="flex h-full flex-col">
        {allTimeTotal === 0 ? (
          <HeatmapEmptyState emptyMessage={emptyMessage} title={title} />
        ) : (
          <div className="group relative flex-grow overflow-hidden rounded-xl border border-border bg-background-secondary p-4">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50" />
            <div className="absolute inset-0 opacity-5">
              <div className="absolute right-0 top-0 h-24 w-24 -translate-y-12 translate-x-12 rounded-full bg-primary" />
              <div className="absolute bottom-0 left-0 h-16 w-16 translate-y-8 -translate-x-8 rounded-full bg-secondary" />
            </div>

            {/* Month total — small, in the corner, so it never reads like a date */}
            <div className="absolute right-4 top-4 z-10 text-right">
              <span className="text-sm font-bold text-content-primary">{month.total}</span>
              <span className="ml-1 text-xs text-content-secondary">total</span>
            </div>

            <div className="relative z-10 flex h-full flex-col">
              <ChartHeader title={title} />

              <div className="grid grid-cols-7 gap-1">
                {WEEKDAYS.map((day, index) => (
                  <div key={index} className="p-1 text-center text-xs font-medium text-content-secondary">
                    {day}
                  </div>
                ))}
                {month.days.map((day) => {
                  if (!day.inMonth) {
                    // Adjacent-month filler — keeps the grid a fixed 6 rows without dead space.
                    return (
                      <div
                        key={day.date}
                        className="flex aspect-square items-center justify-center rounded-md text-xs font-medium text-content-secondary opacity-40"
                      >
                        {day.dayOfMonth}
                      </div>
                    );
                  }

                  const value = data[day.date] || 0;
                  const intensity = getIntensity(value);
                  const hasActivity = intensity > 0 && !day.isFuture;
                  // Activity always wins; among empty days, vacation takes precedence over a protected day.
                  const isVacationDay = !day.isFuture && intensity === 0 && vacationDays.has(day.date);
                  const isProtectedDay = !day.isFuture && intensity === 0 && !isVacationDay && protectedDays.has(day.date);

                  // Vacation tints the text/border via the palette purple (theme-aware).
                  const cellStyle = isVacationDay
                    ? { borderColor: 'var(--color-glassy-purple)', color: 'var(--color-glassy-purple)' }
                    : undefined;

                  return (
                    <div
                      key={day.date}
                      className={`
                        relative flex aspect-square items-center justify-center overflow-hidden rounded-md border
                        text-xs font-medium transition-all duration-200 cursor-default
                        ${day.isToday ? 'ring-2 ring-primary ring-offset-1 ring-offset-background-secondary' : ''}
                        ${
                          isVacationDay
                            ? 'border-dashed bg-violet-500/10 hover:bg-violet-500/20' // Vacation (streak paused) — purple via inline var
                            : isProtectedDay
                            ? 'border-dashed border-sky-400 bg-sky-500/10 text-sky-600 dark:text-sky-300 hover:bg-sky-500/20' // Protected day
                            : day.isFuture
                            ? 'border-border bg-background-primary' // Future day — number dimmed on the span below
                            : hasActivity
                            ? 'border-transparent hover:opacity-90' // Activity — color comes from the fill layer below
                            : 'border-border bg-background-primary text-content-secondary transition-colors hover:bg-border/20' // Empty day
                        }
                      `}
                      style={cellStyle}
                      onMouseEnter={(e) => handleMouseEnter(e, day)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {/* Faded color fill — kept on its own layer so the number stays full-opacity white */}
                      {hasActivity && (
                        <span
                          aria-hidden="true"
                          className="absolute inset-0"
                          style={{ backgroundColor: `var(${colorVar})`, opacity: 0.2 + intensity * 0.2 }}
                        />
                      )}
                      <span
                        className={`relative z-10 ${hasActivity ? 'text-white' : ''} ${day.isFuture ? 'text-content-secondary opacity-50' : ''}`}
                      >
                        {day.dayOfMonth}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Footer pinned to the bottom so the month nav lines up across all cards,
                  whether or not the optional plays/review line is present (Items Learned has none). */}
              <div className="mt-auto border-t border-border pt-3">
                {/* Always occupies a line (invisible when there's no ratio) so the divider
                    and month nav stay aligned across cards with and without this stat. */}
                <div className={`mb-3 text-center text-xs text-content-secondary ${month.ratio == null ? 'invisible' : ''}`}>
                  <span className="font-bold text-content-primary">{month.ratio != null ? displayListens(month.ratio).toFixed(1) : '0.0'}</span> plays/review this month
                </div>

                {/* Month navigation */}
                <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setMonthOffset((o) => Math.min(maxOffset, o + 1))}
                  disabled={!canGoOlder}
                  aria-label="Previous month"
                  className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
                    canGoOlder ? 'text-content-secondary hover:bg-border/30' : 'cursor-not-allowed text-content-secondary/25'
                  }`}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>

                <span className="text-xs font-medium text-content-secondary">{month.label}</span>

                <button
                  type="button"
                  onClick={() => setMonthOffset((o) => Math.max(0, o - 1))}
                  disabled={!canGoNewer}
                  aria-label="Next month"
                  className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
                    canGoNewer ? 'text-content-secondary hover:bg-border/30' : 'cursor-not-allowed text-content-secondary/25'
                  }`}
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tooltip */}
      <HeatmapTooltip visible={tooltip.visible} x={tooltip.x} y={tooltip.y} date={tooltip.date} value={tooltip.value} ratio={tooltip.ratio} title={title} isProtectedDay={tooltip.isProtectedDay} isVacationDay={tooltip.isVacationDay} />
    </>
  );
};

export default ReviewHeatmap;
